import { useEffect, useState } from "react";
import { cn } from "./ui";
import { PaneHead } from "./settings-parts";
import { useAuth } from "./auth";
import useFeedback, {
  FEEDBACK_JOURNAL_EVENT,
  commentFeedback,
  deleteFeedback,
  feedbackKindMeta,
  feedbackStatusMeta,
  loadFeedbackItem,
  patchFeedback,
  queueFix,
  screenshotUrl,
  takeJournalRequest,
  titleOf,
  when,
  type FeedbackItem,
  type FeedbackStatus,
} from "./feedback-store";
import { apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Раздел «Журнал замечаний»: идеи и проблемы пользователей со скриншотами (данные — /api/feedback).
   Порядок как в zakup.starec.ai: администратор нажимает «Запустить исправление» → робот Claude Code (tdocbot, раз в 10 минут)
   правит код, выкладывает и ставит «Исправлено — проверьте» → автор принимает или возвращает в работу с комментарием. */

type Filter = "all" | FeedbackStatus;

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: t("Все") },
  { id: "new", label: t("Новые") },
  { id: "work", label: t("В работе") },
  { id: "testing", label: t("Исправлено") },
  { id: "done", label: t("Принято") },
  { id: "reject", label: t("Отклонено") },
];

const PRIORITY: Record<FeedbackItem["priority"], string> = { low: t("низкий"), normal: t("обычный"), high: t("высокий"), critical: t("критичный") };

const FIX_STATE: Record<string, { label: string; tone: string; icon: string }> = {
  queued: { label: t("в очереди робота"), tone: "bg-amber-400/15 text-amber-700", icon: "ri-time-line" },
  running: { label: t("робот исправляет"), tone: "bg-primary/12 text-primary", icon: "ri-loader-4-line animate-spin" },
  failed: { label: t("робот не справился"), tone: "bg-red-500/10 text-red-600", icon: "ri-error-warning-line" },
};

const EVENT_TEXT: Record<string, string> = { status: t("статус"), priority: t("приоритет"), comment: t("комментарий"), fix: t("робот") };

const statusTone = (status: FeedbackStatus) =>
  status === "testing"
    ? "bg-emerald-500/12 text-emerald-600"
    : status === "done"
      ? "bg-emerald-500/20 text-emerald-700"
      : status === "work"
        ? "bg-primary/12 text-primary"
        : status === "reject"
          ? "bg-foreground/[.06] text-muted-foreground"
          : "bg-foreground/[.06] text-foreground/70";

const btn = "inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-2 py-1 text-[10.5px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";

export default function SettingsFeedback({ onFlash }: { onFlash: (text: string) => void }) {
  const { user } = useAuth();
  const { items, counts, reload } = useFeedback();
  const admin = Boolean(user?.can.manageFeedback);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [zoom, setZoom] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState<number | null>(null);
  // замечание, к которому нужно проскроллить, когда оно появится в списке
  const [focusId, setFocusId] = useState<number | null>(null);

  const run = async (id: number, action: () => Promise<unknown>, done: string) => {
    setBusy(id);
    try {
      await action();
      onFlash(done);
      return true;
    } catch (err) {
      onFlash(apiErrorText(err));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (id: number) => {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    setDraft("");
    try {
      await loadFeedbackItem(id);
    } catch (err) {
      onFlash(apiErrorText(err));
    }
  };

  // запрос «открыть замечание №id» от плашки или уведомления: раскрываем историю и показываем его в списке
  useEffect(() => {
    const show = () => {
      const req = takeJournalRequest();
      if (!req?.id) return;
      setFilter("all");
      setQuery("");
      setOpen(req.id);
      setDraft("");
      setFocusId(req.id);
      loadFeedbackItem(req.id).catch((err) => onFlash(apiErrorText(err)));
    };
    show();
    window.addEventListener(FEEDBACK_JOURNAL_EVENT, show);
    return () => window.removeEventListener(FEEDBACK_JOURNAL_EVENT, show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (focusId === null) return;
    const el = document.getElementById(`fb-${focusId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFocusId(null);
  }, [focusId, items]);

  const queued = items.filter((i) => i.fix_state === "queued" || i.fix_state === "running").length;
  const failed = items.filter((i) => i.fix_state === "failed").length;

  const prompt = () => {
    const list = items.filter((i) => i.status === "new" || i.status === "work");
    if (!list.length) return "";
    const rows = list.map((it) => {
      const shot = it.has_screenshot ? t("скриншот: python3 backend/scripts/tickets.py show {id}", { id: it.id }) : t("без скриншота");
      return `#${it.id} [${feedbackKindMeta[it.kind].label}] ${titleOf(it.message)}\n   ${t("Экран")}: ${it.screen || "—"}${it.anchor ? ` (${it.anchor})` : ""} · ${t("окно")} ${it.context?.viewport || "—"} · ${when(it.created_at)} · ${shot}\n   ${t("Текст")}: ${it.message}`;
    });
    return [t("# Журнал замечаний Hub (hub.starec.ai)"), "", t("Исправь по порядку. Подробности и снимок: `python3 backend/scripts/tickets.py show N`; после исправления — `tickets.py status N testing \"что изменилось\"`."), "", ...rows].join("\n");
  };

  const copyPrompt = async () => {
    const text = prompt();
    if (!text) {
      onFlash(t("В журнале нет открытых замечаний"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      onFlash(t("Промпт для Claude Code скопирован"));
    } catch {
      onFlash(t("Браузер не дал доступ к буферу обмена"));
    }
  };

  const visible = items.filter((it) => {
    if (filter !== "all" && it.status !== filter) return false;
    const q = query.trim().toLowerCase().replace(/^#/, "");
    if (!q) return true;
    return `${it.id} ${it.message} ${it.screen ?? ""} ${it.user_name ?? ""}`.toLowerCase().includes(q);
  });

  return (
    <div className="p-3.5 md:p-5">
      <PaneHead
        icon="ri-bug-line"
        title={t("Журнал замечаний")}
        text={
          admin
            ? t("Идеи и проблемы пользователей с плашки обратной связи: скриншот экрана, размер окна и сам текст. «Запустить исправление» передаёт замечание роботу Claude Code.")
            : t("Ваши идеи и проблемы с плашки обратной связи. Когда замечание исправят, проверьте и примите его или верните в работу с комментарием.")
        }
      />

      {/* сводка */}
      <div className="mt-3.5 flex flex-wrap gap-1.5">
        {(["new", "work", "testing", "done", "reject"] as FeedbackStatus[]).map((id) => (
          <span key={id} className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-semibold", statusTone(id))}>
            <i className={cn(feedbackStatusMeta[id].icon, "text-[11px] leading-none")} aria-hidden />
            {feedbackStatusMeta[id].label.toLowerCase()}
            <span className="tabular-nums">{counts[id]}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground/[.04] px-2.5 py-1 text-[10.5px] font-semibold text-muted-foreground">
          {t("всего")} <span className="tabular-nums">{counts.total}</span>
        </span>
      </div>

      {/* робот */}
      {admin ? (
        <section className="mt-3 rounded-btn border border-border bg-background/50 p-3.5">
          <div className="flex flex-wrap items-start gap-2.5">
            <span className="grid size-8 flex-none place-items-center rounded-btn bg-foreground text-background" aria-hidden>
              <i className="ri-robot-2-line text-[15px] leading-none" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[12px] font-semibold leading-snug">
                {t("Робот Claude Code")}
                <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide", queued ? "bg-primary/12 text-primary" : "bg-foreground/[.06] text-muted-foreground")}>
                  {queued ? <i className="ri-loader-4-line animate-spin text-[10px] leading-none" aria-hidden /> : <i className="ri-circle-fill text-[6px] leading-none" aria-hidden />}
                  {queued ? t("в очереди {n}", { n: queued }) : t("очередь пуста")}
                </span>
              </p>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
                {t("Проверяет очередь каждые 10 минут: берёт одно замечание, правит код, прогоняет проверки, выкладывает и ставит «Исправлено — проверьте».")}
                {failed ? ` ${t("Не справился с замечаниями: {n} — см. пометку в карточке.", { n: failed })}` : ""}
              </p>
            </div>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <button type="button" onClick={copyPrompt} className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary">
              <i className="ri-file-copy-line text-[12px] leading-none" aria-hidden />
              {t("Копировать промпт")}
            </button>
            <button type="button" onClick={() => void reload().catch((e) => onFlash(apiErrorText(e)))} className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-2 text-[11px] font-semibold text-muted-foreground transition hover:text-foreground">
              <i className="ri-refresh-line text-[12px] leading-none" aria-hidden />
              {t("Обновить")}
            </button>
            <span className="ms-auto inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <i className="ri-shield-check-line text-[11px] leading-none text-primary" aria-hidden />
              {t("правки выкладываются только после проверок")}
            </span>
          </div>
        </section>
      ) : null}

      {/* фильтры */}
      <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
        <div className="flex flex-wrap gap-1 rounded-full bg-foreground/[.05] px-1 py-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={cn("inline-flex h-6 cursor-pointer items-center whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold transition", filter === f.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ms-auto min-w-0 flex-1 sm:max-w-[13rem]">
          <i className="ri-search-line pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            placeholder={t("Поиск или №")}
            aria-label={t("Поиск по журналу замечаний")}
            className="h-8 w-full box-border rounded-btn border border-border bg-background ps-7 pe-2 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {/* записи */}
      {visible.length === 0 ? (
        <div className="mt-3 flex flex-col items-center gap-1.5 rounded-btn border border-dashed border-border px-4 py-9 text-center">
          <span className="grid size-11 place-items-center rounded-full bg-foreground/[.05]" aria-hidden>
            <i className="ri-inbox-archive-line text-lg leading-none text-muted-foreground" />
          </span>
          <p className="text-[12.5px] font-semibold">{t("Здесь пока пусто")}</p>
          <p className="max-w-[19rem] text-[11px] leading-relaxed text-muted-foreground">
            {t("Нажмите плашку «Идея / проблема» в углу страницы, опишите замечание и приложите скриншот — запись появится в журнале.")}
          </p>
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {visible.map((it) => {
            const kind = feedbackKindMeta[it.kind];
            const status = feedbackStatusMeta[it.status];
            const mine = it.user_id === user?.id;
            const fix = it.fix_state ? FIX_STATE[it.fix_state] : null;
            const openNow = open === it.id;
            const working = busy === it.id;
            const closed = it.status === "done" || it.status === "reject";
            const robotBusy = it.fix_state === "queued" || it.fix_state === "running";
            return (
              <li key={it.id} id={`fb-${it.id}`} className="rounded-btn border border-border bg-background/50 p-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] font-bold text-muted-foreground tabular-nums">{t("№{id}", { id: it.id })}</span>
                  <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[9.5px] font-bold", it.kind === "problem" ? "bg-red-500/10 text-red-600" : "bg-primary/12 text-primary")}>
                    <i className={cn(kind.icon, "text-[11px] leading-none")} aria-hidden />
                    {kind.label}
                  </span>
                  <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[9.5px] font-semibold", statusTone(it.status))}>
                    <i className={cn(status.icon, "text-[10px] leading-none")} aria-hidden />
                    {status.label}
                  </span>
                  {fix ? (
                    <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[9.5px] font-semibold", fix.tone)}>
                      <i className={cn(fix.icon, "text-[10px] leading-none")} aria-hidden />
                      {fix.label}
                    </span>
                  ) : null}
                  {it.priority !== "normal" ? <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[9.5px] font-semibold text-amber-700">{t("приоритет: {p}", { p: PRIORITY[it.priority] })}</span> : null}
                  <span className="ms-auto whitespace-nowrap text-[10px] text-muted-foreground tabular-nums">{when(it.created_at)}</span>
                </div>

                <div className="mt-2 flex items-start gap-2.5">
                  {it.has_screenshot ? (
                    <button type="button" onClick={() => setZoom(screenshotUrl(it.id))} aria-label={t("Открыть скриншот замечания №{id}", { id: it.id })} className="group relative h-14 w-20 flex-none cursor-pointer overflow-hidden rounded-btn border border-border">
                      <img src={screenshotUrl(it.id)} alt={t("Скриншот экрана «{screen}»", { screen: it.screen ?? "" })} loading="lazy" className="absolute inset-0 size-full object-cover object-top" />
                      <span className="absolute inset-0 grid place-items-center bg-black/0 text-white opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
                        <i className="ri-zoom-in-line text-sm leading-none" aria-hidden />
                      </span>
                    </button>
                  ) : (
                    <span className="grid h-14 w-20 flex-none place-items-center rounded-btn border border-dashed border-border text-muted-foreground" aria-hidden>
                      <i className="ri-screenshot-line text-base leading-none" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold leading-snug">{titleOf(it.message)}</p>
                    <p className="mt-1 whitespace-pre-line text-[11px] leading-relaxed text-muted-foreground">{it.message}</p>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1 text-[9.5px] text-muted-foreground">
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-foreground/[.04] px-2 py-0.5">
                    <i className="ri-window-line flex-none text-[11px] leading-none" aria-hidden />
                    <span className="truncate">{it.screen || "—"}</span>
                  </span>
                  {it.context?.viewport ? <span className="whitespace-nowrap rounded-full bg-foreground/[.04] px-2 py-0.5 tabular-nums">{it.context.viewport}</span> : null}
                  <span className="whitespace-nowrap rounded-full bg-foreground/[.04] px-2 py-0.5" title={it.user_login ?? ""}>
                    {mine ? t("вы") : it.user_name}
                  </span>
                  {it.comments ? <span className="whitespace-nowrap rounded-full bg-foreground/[.04] px-2 py-0.5">{t("комментариев: {n}", { n: it.comments })}</span> : null}
                </div>

                {it.fix_note ? (
                  <p className="mt-2 flex items-start gap-1.5 rounded-btn bg-foreground/[.04] px-2 py-1.5 text-[10px] leading-relaxed text-muted-foreground">
                    <i className="ri-robot-2-line mt-[1px] flex-none text-[11px] leading-none" aria-hidden />
                    {it.fix_note}
                  </p>
                ) : null}

                {mine && it.status === "testing" ? (
                  <p className="mt-2 rounded-btn bg-emerald-500/10 px-2 py-1.5 text-[10.5px] leading-relaxed text-emerald-700">
                    {t("Замечание исправлено. Проверьте на экране «{screen}» и нажмите «Принять» или верните в работу с комментарием.", { screen: it.screen || "—" })}
                  </p>
                ) : null}

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {admin && !closed ? (
                    <>
                      <button type="button" disabled={working || robotBusy} onClick={() => void run(it.id, () => queueFix(it.id), t("Замечание №{id} поставлено в очередь робота", { id: it.id }))} className={cn(btn, "bg-primary/12 text-primary hover:bg-primary/20")}>
                        <i className="ri-magic-line text-[11px] leading-none" aria-hidden />
                        {it.fix_state === "failed" ? t("Запустить ещё раз") : t("Запустить исправление")}
                      </button>
                      {it.status !== "testing" ? (
                        <button type="button" disabled={working} onClick={() => void run(it.id, () => patchFeedback(it.id, { status: "testing", comment: draft.trim() || undefined }), t("Отмечено «Исправлено» — автор увидит и проверит"))} className={cn(btn, "text-muted-foreground hover:text-emerald-600")}>
                          <i className="ri-check-line text-[11px] leading-none" aria-hidden />
                          {t("Исправлено")}
                        </button>
                      ) : null}
                      <select
                        value={it.priority}
                        disabled={working}
                        onChange={(e) => void run(it.id, () => patchFeedback(it.id, { priority: e.target.value as FeedbackItem["priority"] }), t("Приоритет изменён"))}
                        aria-label={t("Приоритет")}
                        className="h-6 cursor-pointer rounded-btn border border-border bg-background px-1.5 text-[10.5px] text-foreground"
                      >
                        {Object.entries(PRIORITY).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <button type="button" disabled={working} onClick={() => void run(it.id, () => patchFeedback(it.id, { status: "reject", comment: draft.trim() || undefined }), t("Замечание отклонено"))} className={cn(btn, "ms-auto text-muted-foreground hover:text-foreground")}>
                        <i className="ri-close-line text-[11px] leading-none" aria-hidden />
                        {t("Отклонить")}
                      </button>
                    </>
                  ) : null}
                  {admin && it.status === "reject" ? (
                    <button type="button" disabled={working} onClick={() => void run(it.id, () => patchFeedback(it.id, { status: "new" }), t("Замечание вернули в новые"))} className={cn(btn, "bg-foreground/[.05] hover:text-foreground")}>
                      <i className="ri-arrow-go-back-line text-[11px] leading-none" aria-hidden />
                      {t("Вернуть в новые")}
                    </button>
                  ) : null}
                  {mine && it.status === "testing" ? (
                    <>
                      <button type="button" disabled={working} onClick={() => void run(it.id, () => patchFeedback(it.id, { status: "done" }), t("Спасибо! Замечание принято"))} className={cn(btn, "bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20")}>
                        <i className="ri-check-double-line text-[11px] leading-none" aria-hidden />
                        {t("Принять")}
                      </button>
                      <button
                        type="button"
                        disabled={working}
                        onClick={() => {
                          if (!draft.trim() || !openNow) {
                            if (!openNow) void toggle(it.id);
                            onFlash(t("Напишите в комментарии, что осталось не так, и нажмите «Вернуть в работу» ещё раз"));
                            return;
                          }
                          void run(it.id, () => patchFeedback(it.id, { status: "work", comment: draft.trim() }), t("Замечание возвращено в работу")).then((ok) => ok && setDraft(""));
                        }}
                        className={cn(btn, "text-muted-foreground hover:text-foreground")}
                      >
                        <i className="ri-arrow-go-back-line text-[11px] leading-none" aria-hidden />
                        {t("Вернуть в работу")}
                      </button>
                    </>
                  ) : null}
                  {mine && it.status === "new" && !admin ? (
                    <button type="button" disabled={working} onClick={() => void run(it.id, () => deleteFeedback(it.id), t("Замечание удалено"))} className={cn(btn, "ms-auto text-muted-foreground hover:text-red-600")}>
                      <i className="ri-delete-bin-line text-[11px] leading-none" aria-hidden />
                      {t("Удалить")}
                    </button>
                  ) : null}
                  <button type="button" onClick={() => void toggle(it.id)} aria-expanded={openNow} className={cn(btn, "text-muted-foreground hover:text-primary", !(admin && !closed) && "ms-auto")}>
                    <i className={cn(openNow ? "ri-arrow-up-s-line" : "ri-chat-3-line", "text-[11px] leading-none")} aria-hidden />
                    {openNow ? t("Свернуть") : t("История и комментарии")}
                  </button>
                </div>

                {openNow ? (
                  <div className="mt-2 rounded-btn border border-border bg-card/60 p-2.5">
                    {it.events?.length ? (
                      <ul className="flex flex-col gap-1">
                        {it.events.map((e) => (
                          <li key={e.id} className="text-[10.5px] leading-relaxed">
                            <span className="text-muted-foreground tabular-nums">{when(e.created_at)}</span> · <b className="font-semibold">{e.author || t("система")}</b> · {EVENT_TEXT[e.type] ?? e.type}
                            {e.type === "status" && e.to_value ? ` → ${feedbackStatusMeta[e.to_value as FeedbackStatus]?.label ?? e.to_value}` : ""}
                            {e.type === "priority" && e.to_value ? ` → ${PRIORITY[e.to_value as FeedbackItem["priority"]] ?? e.to_value}` : ""}
                            {e.text ? <span className="block whitespace-pre-line text-muted-foreground">{e.text}</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[10.5px] text-muted-foreground">{t("Истории пока нет.")}</p>
                    )}
                    {!closed ? (
                      <div className="mt-2 flex flex-col gap-1.5 sm:flex-row">
                        <textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          rows={2}
                          maxLength={4000}
                          placeholder={mine && it.status === "testing" ? t("Что осталось не так? Комментарий нужен, чтобы вернуть в работу") : t("Комментарий или вопрос")}
                          className="min-h-[2.6rem] w-full box-border flex-1 resize-y rounded-btn border border-border bg-background px-2 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                        />
                        <button
                          type="button"
                          disabled={working || !draft.trim()}
                          onClick={() => void run(it.id, () => commentFeedback(it.id, draft.trim()), t("Комментарий добавлен")).then((ok) => ok && setDraft(""))}
                          className={cn(btn, "h-8 justify-center bg-primary px-3 text-primary-foreground hover:brightness-110")}
                        >
                          <i className="ri-send-plane-line text-[11px] leading-none" aria-hidden />
                          {t("Отправить")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
        {t("Робот берёт только замечания, которые администратор передал кнопкой «Запустить исправление». «Принято» ставит только автор замечания.")}
      </p>

      {zoom ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label={t("Скриншот замечания")} onClick={() => setZoom(null)}>
          <button type="button" onClick={() => setZoom(null)} aria-label={t("Закрыть скриншот")} className="absolute end-3 top-3 grid size-9 cursor-pointer place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25">
            <i className="ri-close-line text-lg leading-none" aria-hidden />
          </button>
          <img src={zoom} alt={t("Скриншот экрана из журнала замечаний")} className="max-h-[84vh] max-w-full rounded-card object-contain" />
        </div>
      ) : null}
    </div>
  );
}
